/* ═══════════════════════════════════════
   RAJPUTANA CAMISA — theme.js
   Shopify Asset: assets/theme.js
═══════════════════════════════════════ */

'use strict';

/* ── Sticky Header ── */
(function() {
  const header = document.getElementById('site-header');
  if (!header) return;
  window.addEventListener('scroll', () => {
    header.classList.toggle('scrolled', window.scrollY > 50);
  }, { passive: true });
})();

/* ── Scroll Reveal ── */
(function() {
  const obs = new IntersectionObserver((entries) => {
    entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
  }, { threshold: 0.1 });
  document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
})();

/* ── Focus management for drawers / overlays / modals ──
   Keeps keyboard focus inside an open panel and returns it to whatever
   triggered the panel when it closes, so keyboard-only users are never
   dropped back at the top of the page or left tabbing through hidden
   content behind an overlay. */
let rcLastFocusedEl = null;
const RC_FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function rcRememberFocus() {
  rcLastFocusedEl = document.activeElement;
}
function rcRestoreFocus() {
  if (rcLastFocusedEl && document.body.contains(rcLastFocusedEl) && typeof rcLastFocusedEl.focus === 'function') {
    rcLastFocusedEl.focus();
  }
  rcLastFocusedEl = null;
}
function rcFocusFirstIn(container) {
  if (!container) return;
  const target = container.querySelector('[data-autofocus]') || container.querySelector(RC_FOCUSABLE);
  if (target) target.focus();
}

// Trap Tab within whichever panel is currently open. Only one of these
// is ever open at once in this theme (Escape closes all of them), so a
// single shared handler covering all panel selectors is sufficient.
const RC_PANEL_SELECTORS = ['#cart-drawer.open', '#search-overlay.open', '#mobile-nav.open', '#rc-lightbox.open', '#size-guide-modal.open'];
document.addEventListener('keydown', function(e) {
  if (e.key !== 'Tab') return;
  let open = null;
  for (const sel of RC_PANEL_SELECTORS) { open = document.querySelector(sel); if (open) break; }
  if (!open) return;
  const focusables = Array.from(open.querySelectorAll(RC_FOCUSABLE)).filter(el => el.offsetParent !== null || el === document.activeElement);
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault(); last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault(); first.focus();
  }
});

/* ── Mobile Nav ── */
function rcToggleMobileNav() {
  const nav = document.getElementById('mobile-nav');
  const open = nav.classList.toggle('open');
  document.getElementById('mobile-overlay').classList.toggle('open', open);
  document.getElementById('burger-btn').classList.toggle('open', open);
  document.getElementById('burger-btn').setAttribute('aria-expanded', open ? 'true' : 'false');
  document.body.style.overflow = open ? 'hidden' : '';
  if (open) { rcRememberFocus(); rcFocusFirstIn(nav); } else { rcRestoreFocus(); }
}
function rcCloseMobileNav() {
  const nav = document.getElementById('mobile-nav');
  const wasOpen = nav?.classList.contains('open');
  nav?.classList.remove('open');
  document.getElementById('mobile-overlay')?.classList.remove('open');
  document.getElementById('burger-btn')?.classList.remove('open');
  document.getElementById('burger-btn')?.setAttribute('aria-expanded', 'false');
  document.body.style.overflow = '';
  if (wasOpen) rcRestoreFocus();
}

/* ── Cart Drawer ── */
function rcOpenCart() {
  rcRememberFocus();
  document.getElementById('cart-drawer')?.classList.add('open');
  document.getElementById('cart-overlay')?.classList.add('open');
  document.body.style.overflow = 'hidden';
  rcFocusFirstIn(document.getElementById('cart-drawer'));
}
function rcCloseCart() {
  const wasOpen = document.getElementById('cart-drawer')?.classList.contains('open');
  document.getElementById('cart-drawer')?.classList.remove('open');
  document.getElementById('cart-overlay')?.classList.remove('open');
  document.body.style.overflow = '';
  if (wasOpen) rcRestoreFocus();
}

/* ── Search ── */
function rcOpenSearch() {
  rcRememberFocus();
  document.getElementById('search-overlay')?.classList.add('open');
  document.body.style.overflow = 'hidden';
  setTimeout(() => document.querySelector('.search-overlay__input')?.focus(), 100);
}
function rcCloseSearch() {
  const wasOpen = document.getElementById('search-overlay')?.classList.contains('open');
  document.getElementById('search-overlay')?.classList.remove('open');
  document.body.style.overflow = '';
  if (wasOpen) rcRestoreFocus();
}

/* ── Lightbox ── */
function rcOpenLightbox(src) {
  const lb = document.getElementById('rc-lightbox');
  const img = document.getElementById('rc-lightbox-img');
  if (!lb || !img) return;
  rcRememberFocus();
  img.src = src;
  lb.classList.add('open');
  document.body.style.overflow = 'hidden';
  rcFocusFirstIn(lb);
}
function rcCloseLightbox() {
  const lb = document.getElementById('rc-lightbox');
  const wasOpen = lb?.classList.contains('open');
  lb?.classList.remove('open');
  document.body.style.overflow = '';
  if (wasOpen) rcRestoreFocus();
}

/* ── Size Guide Modal (product page) ── */
function rcOpenSizeGuide() {
  const modal = document.getElementById('size-guide-modal');
  if (!modal) return;
  rcRememberFocus();
  modal.classList.add('open');
  document.body.style.overflow = 'hidden';
  rcFocusFirstIn(modal);
}
function rcCloseSizeGuide() {
  const modal = document.getElementById('size-guide-modal');
  const wasOpen = modal?.classList.contains('open');
  modal?.classList.remove('open');
  document.body.style.overflow = '';
  if (wasOpen) rcRestoreFocus();
}

/* ── Keyboard activation for click-only elements (gallery thumbnails,
     lookbook tiles) marked with role="button" + data-lightbox-src ── */
document.addEventListener('keydown', function(e) {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  const el = e.target.closest('[data-lightbox-src]');
  if (!el) return;
  e.preventDefault();
  rcOpenLightbox(el.getAttribute('data-lightbox-src'));
});
document.addEventListener('click', function(e) {
  const el = e.target.closest('[data-lightbox-src]');
  if (!el) return;
  rcOpenLightbox(el.getAttribute('data-lightbox-src'));
});

/* ── Global Escape Key ── */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  rcCloseCart(); rcCloseSearch(); rcCloseLightbox(); rcCloseMobileNav(); rcCloseSizeGuide();
});

/* ── Lightbox: click outside to close ── */
document.getElementById('rc-lightbox')?.addEventListener('click', function(e) {
  if (e.target === this) rcCloseLightbox();
});

/* ── Active Nav Link ── */
(function() {
  const path = window.location.pathname;
  document.querySelectorAll('.site-nav__link, .site-nav__dropdown a, .mobile-nav__links a').forEach(link => {
    if (link.getAttribute('href') === path) link.classList.add('active');
  });
})();

/* ── Cart AJAX: Add to Cart ── */
document.addEventListener('click', function(e) {
  const btn = e.target.closest('.product-card__atc, .product-atc');
  if (!btn || btn.disabled) return;
  e.preventDefault();

  const variantId = btn.dataset.variantId
    || document.getElementById('product-variant-id')?.value;
  const quantity = parseInt(document.getElementById('qty')?.value || 1);
  if (!variantId) return;

  btn.textContent = 'Adding...';
  btn.disabled = true;

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity })
  })
  .then(r => r.json())
  .then(() => {
    btn.textContent = 'Added!';
    rcRefreshCart();
    setTimeout(() => { btn.textContent = 'Add to Bag'; btn.disabled = false; }, 1500);
  })
  .catch(() => { btn.textContent = 'Error'; btn.disabled = false; });
});

/* ── Cart AJAX: Refresh Drawer ── */
function rcRefreshCart() {
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      // Update count
      const countEl = document.getElementById('cart-count');
      if (countEl) {
        countEl.textContent = cart.item_count;
        countEl.classList.toggle('cart-count--hidden', cart.item_count === 0);
      }
      // Refresh drawer body via sections API
      return fetch('/?sections=cart-drawer');
    })
    .then(r => r.json())
    .then(sections => {
      const body = document.getElementById('cart-drawer-body');
      if (body && sections['cart-drawer']) {
        body.innerHTML = sections['cart-drawer'];
      }
      rcOpenCart();
    })
    .catch(console.error);
}

/* ── Cart AJAX: Remove Item ── */
document.addEventListener('click', function(e) {
  const btn = e.target.closest('.cart-item__remove');
  if (!btn) return;
  const key = btn.dataset.key;
  if (!key) return;
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  })
  .then(() => rcRefreshCart())
  .catch(console.error);
});

/* ── Cart AJAX: Qty Change ── */
document.addEventListener('click', function(e) {
  const btn = e.target.closest('.qty-btn[data-action]');
  if (!btn) return;
  const key = btn.dataset.key;
  const action = btn.dataset.action;
  const qtyEl = btn.closest('.cart-item__qty')?.querySelector('.qty-value');
  if (!key || !qtyEl) return;
  let qty = parseInt(qtyEl.textContent) || 1;
  qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: qty })
  })
  .then(() => rcRefreshCart())
  .catch(console.error);
});

/* ── Product Page: Variant Selector ── */
(function() {
  const optionBtns = document.querySelectorAll('.option-btn');
  if (!optionBtns.length) return;

  optionBtns.forEach(btn => {
    btn.addEventListener('click', function() {
      // Deselect siblings
      const pos = this.dataset.optionPosition;
      document.querySelectorAll(`.option-btn[data-option-position="${pos}"]`).forEach(b => { b.classList.remove('selected'); b.setAttribute('aria-pressed', 'false'); });
      this.classList.add('selected');
      this.setAttribute('aria-pressed', 'true');
      rcUpdateVariant();
    });
  });

  function rcUpdateVariant() {
    const selected = {};
    document.querySelectorAll('.option-btn.selected').forEach(btn => {
      selected[btn.dataset.optionPosition] = btn.dataset.value;
    });
    // Find matching variant from window.productVariants (set in product section)
    const variants = window.productVariants;
    if (!variants) return;
    const match = variants.find(v =>
      v.options.every((opt, i) => selected[String(i + 1)] === opt)
    );
    if (!match) return;
    document.getElementById('product-variant-id').value = match.id;
    const priceEl = document.getElementById('product-price');
    if (priceEl) priceEl.textContent = rcFormatMoney(match.price);
    const atcBtn = document.querySelector('.product-atc');
    if (atcBtn) {
      atcBtn.disabled = !match.available;
      atcBtn.textContent = match.available ? 'Add to Bag' : 'Sold Out';
      atcBtn.dataset.variantId = match.id;
    }
  }
})();

/* ── Money Formatter ── */
function rcFormatMoney(cents) {
  return '₹' + (cents / 100).toFixed(2).replace(/\.00$/, '');
}

/* ── Product Page: Related Products ── */
(function() {
  const el = document.getElementById('related-products');
  if (!el) return;
  fetch(el.dataset.url)
    .then(r => r.text())
    .then(html => {
      el.innerHTML = html;
      // Content injected after page load was never seen by the
      // IntersectionObserver set up above, so .reveal elements in here
      // (e.g. the section heading) would otherwise stay opacity:0 forever.
      // Show them immediately instead of trying to scroll-animate them in.
      el.querySelectorAll('.reveal').forEach((r) => r.classList.add('visible'));
    })
    .catch(console.error);
})();

/* ── Product Page Qty Buttons ── */
(function() {
  const dec = document.getElementById('qty-decrease');
  const inc = document.getElementById('qty-increase');
  const inp = document.getElementById('qty');
  if (!dec || !inc || !inp) return;
  dec.addEventListener('click', () => { if (parseInt(inp.value) > 1) inp.value = parseInt(inp.value) - 1; });
  inc.addEventListener('click', () => { inp.value = parseInt(inp.value) + 1; });
})();
